import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { fetchConfig } from '@theme/utilities';

/**
 * Gift Finder quiz (sections/wt-gift-finder.liquid).
 *
 * Four steps (recipient, interest, skill level, budget). Choosing an answer with mouse or touch moves on
 * automatically; keyboard users move through the options with the arrow keys and continue with the
 * "Next" button, which appears once the step has an answer (WCAG 3.2.2). The result scores
 * every product in the server-rendered pool and shows the top three with the reasons they matched.
 *
 * Scoring (higher is better):
 * - interest: +40 when the product is in one of the interest's collections ("Surprise me": +10 for all)
 * - skill level: exact +25, one level off +8, two off -12; "Not sure" favours Beginner/Intermediate
 * - budget: inside the range +25, up to 15 % above +4, otherwise -40
 * - recipient: preferred collection +12, kit age above the recipient's max age -60, "easier" shifts level
 * - quality: rating (up to +10), number of reviews (up to +8), boost collection +8, best-seller rank (up to +10)
 * - products without a difficulty level (add-ons, link packs) -15
 *
 * URL parameters pre-answer the quiz (see #applyDeeplink): ?for=him&love=machines&level=beginner&budget=mid.
 * The quiz opens on the first unanswered question, or straight on the results when all four are set.
 *
 * Add to cart follows Horizon's product form contract (CartLinesUpdateEvent with a deferred promise),
 * so the cart drawer, cart icon and reward bar update and the drawer opens.
 */
class WtGiftFinder extends HTMLElement {
  /** @type {Array<Record<string, any>>} */
  products = [];
  step = 1;

  connectedCallback() {
    try {
      const data = this.querySelector('script[data-products]')?.textContent;
      this.products = data ? JSON.parse(data) : [];
    } catch (error) {
      this.products = [];
    }

    this.form = this.querySelector('[data-quiz]');
    this.results = this.querySelector('[data-results]');
    this.form?.addEventListener('change', this.#handleChange);
    this.form?.addEventListener('pointerdown', this.#handlePointerDown);
    this.form?.addEventListener('keydown', this.#handleKeyDown);
    this.addEventListener('click', this.#handleClick);
    this.#applyDeeplink();
  }

  /**
   * Pre-answers the quiz from `?for=&love=&level=&budget=` (Shop-by chips, recipient tiles, blog teasers).
   * A value matches an answer's value or its key (the label, handleized), or one or more whole words of
   * the key, so `for=teen` finds "A teen" and `love=machines` finds "Machines & mechanisms". `love` also
   * matches an interest's collection handle, `level` the aliases below. Unknown values are ignored, and
   * the kids answer is never pre-selected from a link.
   */
  #applyDeeplink() {
    if (!this.form) return;
    const params = new URLSearchParams(window.location.search);
    const questions = [
      ['for', 'recipient'],
      ['love', 'interest'],
      ['level', 'level'],
      ['budget', 'budget'],
    ];

    let preset = false;
    for (const [param, name] of questions) {
      const value = params.get(param);
      const input = value ? this.#findAnswer(name, value) : null;
      if (input) {
        input.checked = true;
        preset = true;
      }
    }
    if (!preset) return;

    const open = questions.findIndex(([, name]) => !this.#selected(name));
    if (open === -1) {
      this.step = 4;
      this.#showResults();
    } else if (open > 0) {
      this.#goTo(open + 1);
    }
  }

  /**
   * @param {string} name - radio group
   * @param {string} value - raw URL value
   * @returns {HTMLInputElement | null}
   */
  #findAnswer(name, value) {
    let key = value
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
    if (!key) return null;
    if (name === 'level') key = WtGiftFinder.#levelAliases[key] ?? key;

    const inputs = Array.from(this.form?.querySelectorAll(`input[name="${name}"]`) || []).filter(
      (input) => input instanceof HTMLInputElement && !this.#isKidsAnswer(input)
    );
    /** @param {Element} input */
    const exact = (input) =>
      input instanceof HTMLInputElement &&
      (input.value === key ||
        input.dataset.key === key ||
        (name === 'interest' && (input.dataset.collections || '').split(',').includes(key)));
    /** @param {Element} input */
    const words = (input) =>
      key.length >= 3 && input instanceof HTMLElement && `-${input.dataset.key || ''}-`.includes(`-${key}-`);

    const match = inputs.find(exact) || inputs.find(words);
    return match instanceof HTMLInputElement ? match : null;
  }

  /**
   * The kids answer (recipient age limit under 14, or a "kids" label) stays a manual choice.
   * @param {HTMLInputElement} input
   */
  #isKidsAnswer(input) {
    if (input.name !== 'recipient') return false;
    const maxAge = Number(input.dataset.maxAge || 0);
    return (maxAge > 0 && maxAge < 14) || /(^|-)(kid|kids|child|children)(-|$)/.test(input.dataset.key || '');
  }

  /** `level` URL values besides the answer keys; question 3 values are 1–3 and 0 for "Not sure". */
  static #levelAliases = /** @type {Record<string, string>} */ ({
    beginner: '1',
    easy: '1',
    intermediate: '2',
    medium: '2',
    advanced: '3',
    expert: '3',
    hard: '3',
    'not-sure': '0',
    unsure: '0',
    any: '0',
  });

  disconnectedCallback() {
    this.form?.removeEventListener('change', this.#handleChange);
    this.form?.removeEventListener('pointerdown', this.#handlePointerDown);
    this.form?.removeEventListener('keydown', this.#handleKeyDown);
    this.removeEventListener('click', this.#handleClick);
    clearTimeout(this.advanceTimer);
  }

  /** Remember that the next selection comes from a mouse or touch, not the arrow keys. */
  #handlePointerDown = (/** @type {PointerEvent} */ event) => {
    this.pointerSelecting = event.target instanceof Element && Boolean(event.target.closest('.wt-gf__option'));
  };

  /** A pointerdown that turned into a scroll must not make the next keyboard selection auto-advance. */
  #handleKeyDown = () => {
    this.pointerSelecting = false;
  };

  #advance() {
    clearTimeout(this.advanceTimer);
    // A short pause so the selection is visible before the next question slides in.
    this.advanceTimer = setTimeout(() => (this.step < 4 ? this.#goTo(this.step + 1) : this.#showResults()), 220);
  }

  /** @param {Event} event */
  #handleChange = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return;
    this.#updateNext();
    if (this.pointerSelecting) this.#advance();
    this.pointerSelecting = false;
  };

  #updateNext() {
    const next = this.querySelector('[data-next]');
    if (!(next instanceof HTMLButtonElement)) return;
    const answered = Boolean(this.querySelector(`[data-step="${this.step}"] input:checked`));
    next.hidden = !answered;
    next.textContent = (this.step < 4 ? next.dataset.nextLabel : next.dataset.resultsLabel) || next.textContent;
  }

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    // Clicking (or tapping) the answer that's already selected after going back: no change event fires.
    const option = target.closest('.wt-gf__option');
    if (option && !target.closest('input') && event.detail > 0) {
      const input = option.querySelector('input');
      if (input instanceof HTMLInputElement && input.checked) this.#advance();
      return;
    }

    if (target.closest('[data-next]')) {
      this.#advance();
    } else if (target.closest('[data-back]')) {
      this.#goTo(Math.max(1, this.step - 1));
    } else if (target.closest('[data-restart]')) {
      this.#restart();
    } else {
      const add = target.closest('[data-add]');
      if (add instanceof HTMLButtonElement) this.#addToCart(add);
    }
  };

  /** @param {number} step */
  #goTo(step) {
    this.step = step;
    for (const fieldset of this.querySelectorAll('[data-step]')) {
      if (fieldset instanceof HTMLElement) fieldset.hidden = Number(fieldset.dataset.step) !== step;
    }

    const back = this.querySelector('[data-back]');
    if (back instanceof HTMLElement) back.hidden = step === 1;
    this.#updateNext();

    const number = this.querySelector('[data-step-number]');
    if (number) number.textContent = String(step);

    const progress = this.querySelector('[data-progress]');
    if (progress instanceof HTMLElement) {
      progress.setAttribute('aria-valuenow', String(step));
      progress.querySelector('span')?.style.setProperty('--wt-gf-progress', `${step * 25}%`);
    }

    const current = this.querySelector(`[data-step="${step}"]`);
    const focusTarget = current?.querySelector('input:checked') || current?.querySelector('input');
    if (focusTarget instanceof HTMLElement) focusTarget.focus({ preventScroll: true });
    this.#scrollIntoViewIfNeeded(this.form);
  }

  #restart() {
    this.form?.reset();
    if (this.results) this.results.hidden = true;
    if (this.form instanceof HTMLElement) this.form.hidden = false;
    this.#goTo(1);
  }

  /** @param {Element | null | undefined} element */
  #scrollIntoViewIfNeeded(element) {
    if (!(element instanceof HTMLElement)) return;
    const top = element.getBoundingClientRect().top;
    if (top < 0 || top > window.innerHeight * 0.6) {
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      element.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
    }
  }

  /** @param {string} name */
  #selected(name) {
    const input = this.form?.querySelector(`input[name="${name}"]:checked`);
    return input instanceof HTMLInputElement ? input : null;
  }

  #answers() {
    const recipient = this.#selected('recipient');
    const interest = this.#selected('interest');
    const level = this.#selected('level');
    const budget = this.#selected('budget');

    return {
      recipientLabel: recipient?.closest('label')?.querySelector('.wt-gf__option-label')?.textContent?.trim() || '',
      maxAge: Number(recipient?.dataset.maxAge || 0),
      easier: recipient?.dataset.shift === 'easier',
      recipientCollection: recipient?.dataset.collection || '',
      interestLabel: interest?.dataset.label || '',
      interestCollections: (interest?.dataset.collections || '').split(',').filter(Boolean),
      interestUrl: interest?.dataset.url || '',
      level: Number(level?.value || 0),
      levelLabel: level?.closest('label')?.querySelector('.wt-gf__option-label')?.textContent?.trim() || '',
      budget: budget?.value || 'any',
      budgetLabel: budget?.dataset.label || '',
    };
  }

  /**
   * @param {Record<string, any>} product
   * @param {number} index - position in the pool (best sellers first)
   * @param {ReturnType<WtGiftFinder['#answers']>} answers
   */
  #score(product, index, answers) {
    const b1 = Number(this.dataset.b1 || 5000);
    const b2 = Number(this.dataset.b2 || 10000);
    const members = Array.isArray(product.m) ? product.m : [];
    let score = 0;
    /** @type {string[]} */
    const reasons = [];

    // Interest
    if (answers.interestCollections.length) {
      if (answers.interestCollections.some((handle) => members.includes(handle))) {
        score += 40;
        reasons.push(answers.interestLabel);
      }
    } else {
      score += 10;
    }

    // Skill level (recipient "easier" lowers the target by one level)
    const level = Number(product.l || 0);
    let target = answers.level;
    if (answers.easier && target > 1) target -= 1;
    if (!level) {
      score -= 15;
    } else if (target) {
      const distance = Math.abs(level - target);
      score += distance === 0 ? 25 : distance === 1 ? 8 : -12;
      if (distance === 0 && product.ll) reasons.push(product.ll);
    } else {
      // "Not sure": anyone can finish Beginner and Intermediate kits
      score += level === 1 ? 18 : level === 2 ? 14 : -6;
      if (level <= 2 && product.ll) reasons.push(product.ll);
    }
    if (answers.easier && level === 3) score -= 20;

    // Budget
    const price = Number(product.p || 0);
    const ranges = { low: [0, b1], mid: [b1, b2], high: [b2, Infinity], any: [0, Infinity] };
    const [min, max] = ranges[/** @type {'low'|'mid'|'high'|'any'} */ (answers.budget)] || ranges.any;
    if (price >= min && price <= max) {
      score += answers.budget === 'any' ? 0 : 25;
      if (answers.budget !== 'any' && answers.budgetLabel) reasons.push(answers.budgetLabel);
    } else if (price > max && price <= max * 1.15) {
      score += 4;
    } else {
      score -= 40;
    }

    // Recipient
    if (answers.recipientCollection && members.includes(answers.recipientCollection)) score += 12;
    const age = Number(product.a || 0);
    if (answers.maxAge && age > answers.maxAge) score -= 60;

    // Quality signals
    const rating = Number(product.r || 0);
    const count = Number(product.rc || 0);
    if (rating) score += Math.max(0, (rating - 4) * 10);
    if (count) score += Math.min(8, Math.log10(count + 1) * 4);
    if (this.dataset.boost && members.includes(this.dataset.boost)) score += 8;
    score += Math.max(0, 10 - index * 0.25);

    // Build time as a friendly extra reason
    if (product.h && reasons.length < 3) {
      reasons.push((this.dataset.hoursFormat || '~[hours] hrs').replace('[hours]', String(product.h)));
    }

    return { score, reasons: reasons.slice(0, 3) };
  }

  #showResults() {
    const answers = this.#answers();
    const ranked = this.products
      .map((product, index) => ({ product, ...this.#score(product, index, answers) }))
      .sort((a, b) => b.score - a.score);
    const top = ranked.slice(0, 3);
    const noGoodMatch = !top.length || top[0].score < 20;

    this.#renderSummary(answers);
    this.#renderGrid(top, noGoodMatch);

    const seeAll = this.querySelector('[data-see-all]');
    if (seeAll instanceof HTMLAnchorElement) {
      const base = answers.interestUrl || this.dataset.poolUrl || '/collections/all';
      const url = new URL(base, window.location.origin);
      const b1 = Number(this.dataset.b1 || 0) / 100;
      const b2 = Number(this.dataset.b2 || 0) / 100;
      if (answers.budget === 'low') url.searchParams.set('filter.v.price.lte', String(b1));
      if (answers.budget === 'mid') {
        url.searchParams.set('filter.v.price.gte', String(b1));
        url.searchParams.set('filter.v.price.lte', String(b2));
      }
      if (answers.budget === 'high') url.searchParams.set('filter.v.price.gte', String(b2));
      seeAll.href = url.pathname + url.search;
    }

    if (this.form instanceof HTMLElement) this.form.hidden = true;
    if (this.results) {
      this.results.hidden = false;
      this.results.focus({ preventScroll: true });
      this.#scrollIntoViewIfNeeded(this.results);
    }
  }

  /** @param {ReturnType<WtGiftFinder['#answers']>} answers */
  #renderSummary(answers) {
    const summary = this.querySelector('[data-summary]');
    if (!summary) return;
    summary.replaceChildren(
      ...[answers.recipientLabel, answers.interestLabel, answers.levelLabel, answers.budgetLabel]
        .filter(Boolean)
        .map((text) => {
          const li = document.createElement('li');
          li.textContent = text;
          return li;
        })
    );
  }

  /**
   * @param {Array<{ product: Record<string, any>, reasons: string[] }>} items
   * @param {boolean} noGoodMatch
   */
  #renderGrid(items, noGoodMatch) {
    const grid = this.querySelector('[data-grid]');
    if (!grid) return;
    grid.replaceChildren();

    if (noGoodMatch) {
      const note = document.createElement('li');
      note.className = 'wt-gf__empty';
      note.style.gridColumn = '1 / -1';
      note.textContent = (items.length ? this.dataset.emptyText : this.dataset.emptyPoolText) || '';
      grid.append(note);
    }

    items.forEach(({ product, reasons }, index) => {
      const li = document.createElement('li');
      li.className = `wt-gf__card ${index === 0 && !noGoodMatch ? 'wt-gf__card--best' : 'wt-gf__card--alt'}`;

      const badge = document.createElement('span');
      badge.className = 'wt-gf__badge';
      badge.textContent = index === 0 && !noGoodMatch ? this.dataset.bestLabel || '' : this.dataset.altLabel || '';
      if (badge.textContent) li.append(badge);

      const media = document.createElement('a');
      media.className = 'wt-gf__card-media';
      media.href = product.u;
      media.tabIndex = -1;
      media.setAttribute('aria-hidden', 'true');
      if (product.i) {
        const img = document.createElement('img');
        img.src = product.i;
        img.alt = '';
        img.loading = 'lazy';
        img.width = 600;
        img.height = 600;
        media.append(img);
      }

      const body = document.createElement('div');
      body.className = 'wt-gf__card-body';

      const title = document.createElement('h3');
      title.className = 'wt-gf__card-title';
      const link = document.createElement('a');
      link.href = product.u;
      link.textContent = product.t;
      title.append(link);
      body.append(title);

      const rating = Number(product.r || 0);
      if (rating) {
        const row = document.createElement('p');
        row.className = 'wt-gf__rating';
        const stars = document.createElement('span');
        stars.className = 'wt-gf__rating-stars';
        stars.setAttribute('aria-hidden', 'true');
        stars.textContent = '★★★★★';
        const text = document.createElement('span');
        const count = Number(product.rc || 0);
        text.textContent = `${rating.toFixed(1)} ${count ? (this.dataset.reviewsFormat || '([count])').replace('[count]', String(count)) : ''}`.trim();
        text.setAttribute('aria-label', `Rated ${rating.toFixed(1)} out of 5${count ? `, ${count} reviews` : ''}`);
        row.append(stars, text);
        body.append(row);
      }

      if (reasons.length) {
        const list = document.createElement('ul');
        list.className = 'wt-gf__reasons';
        list.setAttribute('role', 'list');
        for (const reason of reasons) {
          const item = document.createElement('li');
          item.textContent = `✓ ${reason}`;
          list.append(item);
        }
        body.append(list);
      }

      const price = document.createElement('p');
      price.className = 'wt-gf__price';
      const current = document.createElement('span');
      current.textContent = product.pf;
      if (Number(product.c) > Number(product.p)) {
        current.className = 'wt-gf__price-sale';
        const compare = document.createElement('s');
        compare.textContent = product.cf;
        const save = document.createElement('span');
        save.className = 'wt-gf__save';
        const percent = Math.floor(((product.c - product.p) / product.c) * 100);
        save.textContent = (this.dataset.saveFormat || 'Save [percent]%').replace('[percent]', String(percent));
        price.append(current, compare, save);
      } else {
        price.append(current);
      }
      body.append(price);

      const actions = document.createElement('div');
      actions.className = 'wt-gf__card-actions';
      const add = document.createElement('button');
      add.type = 'button';
      add.className = 'button wt-gf__add';
      add.dataset.add = String(product.v);
      add.textContent = this.dataset.addLabel || 'Add to cart';
      const view = document.createElement('a');
      view.className = 'wt-link';
      view.href = product.u;
      view.textContent = this.dataset.viewLabel || 'View details';
      actions.append(add, view);
      body.append(actions);

      li.append(media, body);
      grid.append(li);
    });
  }

  /** @param {HTMLButtonElement} button */
  async #addToCart(button) {
    const variantId = Number(button.dataset.add);
    if (!variantId || button.disabled) return;

    button.disabled = true;
    button.setAttribute('aria-busy', 'true');

    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : null))
      .filter(Boolean);

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: String(variantId), quantity: 1 }],
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(
        Theme.routes.cart_add_url,
        fetchConfig('json', {
          body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }], sections: sectionIds.join(',') }),
        })
      );
      const data = await response.json();
      if (data.status) throw new Error(data.description || data.message || 'Add to cart failed');

      const cartResponse = await fetch(`${Theme.routes.cart_url}.js`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      const cart = await cartResponse.json();

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'wt-gift-finder',
          sourceId: this.dataset.sectionId,
          itemCount: 1,
          sections: data.sections,
          didError: false,
        },
      });

      button.classList.add('is-added');
      button.textContent = this.dataset.addedLabel || 'Added';
    } catch (error) {
      deferred.reject(error);
      this.dispatchEvent(
        new CartErrorEvent({
          error: error instanceof Error ? error.message : 'Add to cart failed',
          code: 'INVALID',
          detail: { source: 'wt-gift-finder' },
        })
      );
      button.disabled = false;
      button.textContent = this.dataset.errorLabel || 'Try again';
    } finally {
      button.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('wt-gift-finder')) {
  customElements.define('wt-gift-finder', WtGiftFinder);
}
